import React from "react";
import { beginDecision, PendingDecision } from "../../lib/decisionIdentity";
import { ApiError } from "../../lib/api";
import { contentReviewService, ContentReviewItem, ContentGridVerdict, ContentVerdictResponse } from "../../services/contentReviewService";

export interface SocialsDecisionState {
  kind: "busy" | "unknown" | "error" | "settled";
  message: string;
}
export function useSocialsDecisions() {
  const pending = React.useRef(new Map<string, PendingDecision>());
  const inFlight = React.useRef(new Set<string>());
  const [states, setStates] = React.useState<Record<string, SocialsDecisionState>>({});
  const update = (id: string, state: SocialsDecisionState) => setStates(current => ({ ...current, [id]: state }));
  const decide = async (item: ContentReviewItem, verdict: ContentGridVerdict, note?: string | null): Promise<ContentVerdictResponse | undefined> => {
    const begun = beginDecision(pending.current.get(item.id), verdict,
      verdict === "P" || verdict === "H" ? null : note ?? null,
      () => window.crypto?.randomUUID?.() ?? `socials-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      inFlight.current.has(item.id));
    if (!begun.decision) return;
    const decision = begun.decision;
    pending.current.set(item.id, decision);
    inFlight.current.add(item.id);
    update(item.id, { kind: "busy", message: "Saving decision…" });
    try {
      const response = await contentReviewService.verdict(item.id, verdict, decision.requestId, decision.note);
      // Keep queued identities too: a second approve must replay, never file another decision.
      if (["conflict", "exhausted", "refused"].includes(response.state)) pending.current.delete(item.id);
      update(item.id, { kind: "settled", message: verdict === "P" && (response.route === "box" || ["queued", "leased", "retry"].includes(response.state))
        ? "approved — waiting for the box"
        : response.state === "applied" ? "Decision recorded"
        : response.state === "conflict" ? "Decision conflict — nothing changed"
        : response.state === "refused" || response.state === "exhausted" ? "Couldn’t apply this decision — review needed"
        : "Decision saved — waiting for the box" });
      return response;
    } catch (error) {
      // Transport errors and 5xx may follow a committed write. Only a definitive
      // client rejection allows forgetting the request identity.
      const rejected = error instanceof ApiError && [400, 401, 403, 404, 409, 422].includes(error.status);
      if (rejected) pending.current.delete(item.id);
      update(item.id, { kind: rejected ? "error" : "unknown", message: rejected
        ? "Decision was not accepted. Refresh and try again."
        : "Status unknown — retry safely" });
      return undefined;
    } finally { inFlight.current.delete(item.id); }
  };
  const retry = (item: ContentReviewItem) => {
    const saved = pending.current.get(item.id);
    return saved ? decide(item, saved.verdict as ContentGridVerdict, saved.note) : Promise.resolve(undefined);
  };
  const reconcile = React.useCallback((items: ContentReviewItem[]) => {
    const resolved = items.filter(item => item.command && pending.current.get(item.id)?.requestId === item.command.request_id);
    if (!resolved.length) return;
    for (const item of resolved) {
      if (["conflict","exhausted","refused"].includes(item.command!.state)) pending.current.delete(item.id);
    }
    setStates(current => {
      if (!resolved.some(item => item.id in current)) return current;
      const next = {...current};
      for (const item of resolved) delete next[item.id];
      return next;
    });
  }, []);
  return { states, decide, retry, reconcile };
}
